import { useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, CheckCircle2, Loader2, Mail, Sparkles } from 'lucide-react';
import {
  DEMO_SECTORS,
  DEMO_SECTOR_LABELS,
  demoRequestSchema,
  type DemoRequestInput,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../lib/api';
import { Modal } from './shared/Modal';

/**
 * Modal du formulaire public « Demander une démo » (landing page).
 *
 * Soumet `POST /demo-requests` (route publique, sans auth) :
 *  - persiste la demande (consultable par le super-admin plateforme),
 *  - déclenche une notification email à l'équipe via Resend.
 *
 * `source` identifie le CTA d'origine (header / hero / cta_final) pour le suivi.
 */

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

export function RequestDemoModal({
  source = 'landing',
  onClose,
}: {
  source?: string;
  onClose(): void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<DemoRequestInput>({
    resolver: zodResolver(demoRequestSchema) as unknown as Resolver<DemoRequestInput>,
    defaultValues: {
      full_name: '',
      email: '',
      organization: '',
      phone: '',
      sector: undefined,
      message: '',
    },
  });

  async function onSubmit(values: DemoRequestInput) {
    setSubmitting(true);
    setErrMsg(null);
    try {
      await api.post('/demo-requests', { ...values, source });
      setDone(true);
    } catch (err) {
      setErrMsg(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  // ─── État succès ───────────────────────────────────────────────────────
  if (done) {
    return (
      <Modal
        title="Demande envoyée"
        titleIcon={<CheckCircle2 className="w-5 h-5 text-green-600" />}
        width="lg"
        onClose={onClose}
        footer={
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-500 hover:bg-primary-600"
          >
            Fermer
          </button>
        }
      >
        <div className="text-center py-4">
          <div className="w-14 h-14 mx-auto rounded-full bg-green-50 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-7 h-7 text-green-600" />
          </div>
          <h3 className="text-base font-semibold text-gray-900">Merci, c'est noté&nbsp;!</h3>
          <p className="mt-2 text-sm text-gray-600 max-w-sm mx-auto">
            Votre demande de démo a bien été enregistrée. Notre équipe vous recontacte très
            rapidement pour organiser une présentation adaptée à votre métier.
          </p>
        </div>
      </Modal>
    );
  }

  // ─── État formulaire ───────────────────────────────────────────────────
  return (
    <Modal
      title="Demander une démo"
      titleIcon={<Sparkles className="w-5 h-5 text-primary-600" />}
      width="xl"
      locked={submitting}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="request-demo-form"
            disabled={submitting}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Mail className="w-4 h-4 mr-2" />
            )}
            Envoyer la demande
          </button>
        </>
      }
    >
      <form id="request-demo-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <p className="text-sm text-gray-600">
          Laissez-nous vos coordonnées : nous vous recontactons pour planifier une démo
          personnalisée.
        </p>

        {errMsg && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{errMsg}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Nom complet *" error={errors.full_name?.message}>
            <input
              {...register('full_name')}
              placeholder="Jean Dupont"
              className={inputClass}
              autoFocus
            />
          </Field>
          <Field label="Email professionnel *" error={errors.email?.message}>
            <input
              type="email"
              {...register('email')}
              placeholder="jean.dupont@entreprise.fr"
              className={inputClass}
            />
          </Field>
          <Field label="Organisation" error={errors.organization?.message}>
            <input
              {...register('organization')}
              placeholder="Nom de votre établissement"
              className={inputClass}
            />
          </Field>
          <Field label="Téléphone" error={errors.phone?.message}>
            <input {...register('phone')} placeholder="+33 6 00 00 00 00" className={inputClass} />
          </Field>
          <Field label="Secteur" error={errors.sector?.message} className="md:col-span-2">
            <select {...register('sector')} className={inputClass} defaultValue="">
              <option value="">— Sélectionner —</option>
              {DEMO_SECTORS.map((s) => (
                <option key={s} value={s}>
                  {DEMO_SECTOR_LABELS[s]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Message" error={errors.message?.message} className="md:col-span-2">
            <textarea
              {...register('message')}
              rows={3}
              placeholder="Vos besoins, vos contraintes, le nombre d'utilisateurs…"
              className={inputClass}
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
}

function Field({
  label,
  error,
  className,
  children,
}: {
  label: string;
  error?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}
